import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ProfileVerificationsHeader } from "./ProfileVerificationsHeader";
import { ProfileVerificationsTable } from "./ProfileVerificationsTable";
import RejectReasonModal from "../../../components/admin/pendingApprovals/RejectReasonModal";
import {
  fetchPendingApprovals,
  approveUser,
  rejectUser,
} from "../../../features/admin/adminSlice";

export const ProfileVerificationsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const {
    pendingApprovals,
    pagination,
    loading,
    error,
    approvalLoading,
  } = useSelector((state) => state.admin);

  useEffect(() => {
    dispatch(fetchPendingApprovals({
      page: 1,
      limit: 10,
      role: roleFilter !== "all" ? roleFilter : undefined
    }));
  }, [dispatch, roleFilter]);

  const handleLoadMore = () => {
    const nextPage = pagination.currentPage + 1;
    dispatch(fetchPendingApprovals({
      page: nextPage,
      limit: 10,
      role: roleFilter !== "all" ? roleFilter : undefined
    }));
  };

  const handleRefresh = () => {
    dispatch(fetchPendingApprovals({
      page: 1,
      limit: 10,
      role: roleFilter !== "all" ? roleFilter : undefined
    }));
  };

  const handleApprove = (id) => {
    dispatch(approveUser(id));
  };

  const handleReject = (id, userName) => {
    setSelectedUser({ id, name: userName });
    setRejectModalOpen(true);
  };

  const handleConfirmReject = (reason) => {
    if (selectedUser) {
      dispatch(rejectUser({ id: selectedUser.id, reason }));
      setSelectedUser(null);
    }
  };

  const handleViewProfile = (id, role) => {
    if (!role || !id) return;

    const routeMap = {
      player: `/admin/player/${id}`,
      coach: `/admin/coach/${id}`,
      scout: `/admin/scout/${id}`,
    };

    const route = routeMap[role.toLowerCase()];
    if (route) {
      navigate(route);
    }
  };

  // Filter by search
  const filteredApprovals = pendingApprovals.filter((approval) => {
    const q = search.toLowerCase();
    const firstName = approval.firstName || '';
    const lastName = approval.lastName || '';
    const email = approval.email || '';
    const fullName = `${firstName} ${lastName}`.toLowerCase();
    return !q || fullName.includes(q) || email.toLowerCase().includes(q);
  });

  return (
    <div className="rounded-3xl bg-jp-card border border-jp-line shadow-sm p-6 space-y-4">
      {error && (
        <div className="p-3 bg-red-900/20 border border-red-500 rounded-lg">
          <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}

      <ProfileVerificationsHeader
        search={search}
        onSearchChange={setSearch}
        role={roleFilter}
        onRoleChange={setRoleFilter}
        onRefresh={handleRefresh}
        loading={loading}
      />

      <ProfileVerificationsTable
        requests={filteredApprovals}
        loading={loading}
        approvalLoading={approvalLoading}
        onApprove={handleApprove}
        onReject={handleReject}
        onViewProfile={handleViewProfile}
        onLoadMore={handleLoadMore}
        pagination={pagination}
      />

      <RejectReasonModal
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={handleConfirmReject}
        userName={selectedUser?.name || ''}
      />
    </div>
  );
};
